import React, { useState, useCallback, memo } from "react";
import styles from './Guarantees.module.scss';

interface Guarantee {
  id: number;
  iconName: string;
  title: string;
  description: string;
  image: string;
}

interface GuaranteesProps {
  title: string;
  emptyImage: string;
  guarantees: Guarantee[];
}

// Inline SVG fallback encoded as base64
const FALLBACK_ICON = "data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMzIiIGhlaWdodD0iMzIiIHZpZXdCb3g9IjAgMCAzMiAzMiIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iMzIiIGhlaWdodD0iMzIiIGZpbGw9IiNFNUU1RTUiLz48cGF0aCBkPSJNMTYgOEMxMS41OCA4IDggMTEuNTggOCAxNkM4IDIwLjQyIDExLjU4IDI0IDE2IDI0QzIwLjQyIDI0IDI0IDIwLjQyIDI0IDE2QzI0IDExLjU4IDIwLjQyIDggMTYgOFpNMTcgMjBIMTVWMThIMTdWMjBaTTE3IDE2SDE1VjEySDE3VjE2WiIgZmlsbD0iIzc1NzU3NSIvPjwvc3ZnPg==";

const MAX_RETRIES = 3;
const INITIAL_RETRY_DELAY = 1000; // 1 second

// Memoized guarantee item component
const GuaranteeItem = memo(({ 
  guarantee, 
  onImageError 
}: { 
  guarantee: Guarantee;
  onImageError: (e: React.SyntheticEvent<HTMLImageElement>, id: number) => void;
}) => (
  <article className={styles.guaranteeItem}>
    <div className={styles.iconWrapper}>
      <img
        src={guarantee.image}
        data-original-src={guarantee.image}
        alt={guarantee.title}
        width={32}
        height={32}
        loading="eager"
        decoding="async"
        fetchpriority="high"
        className={styles.guaranteeIcon}
        onError={(e) => onImageError(e, guarantee.id)}
      />
    </div>
    <div className={styles.textContainer}>
      <h3 className={styles.guaranteeTitle}>{guarantee.title}</h3>
      <p className={styles.guaranteeDescription}>{guarantee.description}</p>
    </div>
  </article>
));

GuaranteeItem.displayName = "GuaranteeItem";

function Guarantees({ title, guarantees }: GuaranteesProps) {
  const [retryCount, setRetryCount] = useState<Record<number, number>>({});

  const handleImageError = useCallback(async (e: React.SyntheticEvent<HTMLImageElement>, guaranteeId: number) => {
    const img = e.currentTarget;
    const currentRetries = retryCount[guaranteeId] || 0;

    if (currentRetries < MAX_RETRIES) {
      // Exponential backoff
      const delay = INITIAL_RETRY_DELAY * Math.pow(2, currentRetries);
      
      setRetryCount(prev => ({
        ...prev,
        [guaranteeId]: currentRetries + 1
      }));

      // Try loading the original image again after delay
      await new Promise(resolve => setTimeout(resolve, delay));
      img.src = img.getAttribute('data-original-src') || img.src;
    } else {
      // If all retries failed, use inline SVG fallback
      img.src = FALLBACK_ICON;
      img.onerror = null;
    }
  }, [retryCount]);

  return (
    <section className={styles.guaranteesSection}>
      <h2 className={styles.mainTitle}>{title}</h2>
      <div className={styles.guaranteesList}>
        {guarantees.map((guarantee) => (
          <GuaranteeItem
            key={guarantee.id}
            guarantee={guarantee}
            onImageError={handleImageError}
          />
        ))}
      </div>
    </section>
  );
}

// Memoize the entire component if props don't change frequently
export default memo(Guarantees);

Guarantees.displayName = "Guarantees";
